import { FilterDimension } from '../types/filters';

// Comprehensive filter dimensions configuration
export const FILTER_DIMENSIONS: Record<string, FilterDimension> = {
  // Date & Time
  dateRange: {
    id: 'dateRange',
    label: 'Date Range',
    type: 'date-range',
    category: 'primary',
  },
  
  // Geographic dimensions
  country: {
    id: 'country',
    label: 'Country/Region',
    type: 'multi-select',
    category: 'primary',
    searchable: true,
    cascadeTo: ['region', 'manager', 'team'],
    values: [
      { value: 'US', label: 'United States', count: 450 },
      { value: 'UK', label: 'United Kingdom', count: 280 },
      { value: 'DE', label: 'Germany', count: 320 },
      { value: 'CA', label: 'Canada', count: 180 },
      { value: 'SG', label: 'Singapore', count: 95 },
      { value: 'PL', label: 'Poland', count: 150 },
      { value: 'IN', label: 'India', count: 220 },
    ],
  },
  
  region: {
    id: 'region',
    label: 'Region',
    type: 'multi-select',
    category: 'secondary',
    dependsOn: ['country'],
    values: [
      { value: 'north-america', label: 'North America' },
      { value: 'europe', label: 'Europe' },
      { value: 'asia-pacific', label: 'Asia Pacific' },
    ],
  },
  
  // Organizational hierarchy
  businessUnit: {
    id: 'businessUnit',
    label: 'Business Unit',
    type: 'multi-select',
    category: 'primary',
    searchable: true,
    cascadeTo: ['department', 'manager'],
    values: [
      { value: 'technology', label: 'Technology', count: 680 },
      { value: 'sales', label: 'Sales & Marketing', count: 420 },
      { value: 'operations', label: 'Operations', count: 350 },
      { value: 'finance', label: 'Finance', count: 180 },
      { value: 'hr', label: 'Human Resources', count: 95 },
      { value: 'legal', label: 'Legal & Compliance', count: 45 },
    ],
  },
  
  department: {
    id: 'department',
    label: 'Function/Department',
    type: 'multi-select',
    category: 'primary',
    searchable: true,
    dependsOn: ['businessUnit'],
    cascadeTo: ['team', 'manager'],
    values: [
      { value: 'engineering', label: 'Engineering', count: 420 },
      { value: 'product', label: 'Product Management', count: 85 },
      { value: 'design', label: 'Design', count: 65 },
      { value: 'data-science', label: 'Data Science', count: 90 },
      { value: 'sales', label: 'Sales', count: 280 },
      { value: 'marketing', label: 'Marketing', count: 140 },
      { value: 'customer-success', label: 'Customer Success', count: 95 },
      { value: 'finance', label: 'Finance', count: 180 },
      { value: 'hr', label: 'Human Resources', count: 95 },
      { value: 'legal', label: 'Legal', count: 45 },
      { value: 'operations', label: 'Operations', count: 350 },
    ],
  },
  
  function: {
    id: 'function',
    label: 'Function',
    type: 'multi-select',
    category: 'secondary',
    values: [
      { value: 'individual-contributor', label: 'Individual Contributor', count: 1200 },
      { value: 'people-manager', label: 'People Manager', count: 450 },
      { value: 'technical-lead', label: 'Technical Lead', count: 180 },
      { value: 'executive', label: 'Executive', count: 45 },
    ],
  },
  
  // Hierarchy levels
  level: {
    id: 'level',
    label: 'Level/Grade',
    type: 'multi-select',
    category: 'secondary',
    values: [
      { value: 'L1', label: 'Level 1 (Entry)', count: 180 },
      { value: 'L2', label: 'Level 2 (Associate)', count: 320 },
      { value: 'L3', label: 'Level 3 (Senior)', count: 450 },
      { value: 'L4', label: 'Level 4 (Staff)', count: 280 },
      { value: 'L5', label: 'Level 5 (Principal)', count: 120 },
      { value: 'L6', label: 'Level 6 (Director)', count: 65 },
      { value: 'L7', label: 'Level 7 (VP)', count: 28 },
      { value: 'L8', label: 'Level 8 (SVP)', count: 12 },
      { value: 'L9', label: 'Level 9 (C-Level)', count: 8 },
    ],
  },
  
  grade: {
    id: 'grade',
    label: 'Grade',
    type: 'multi-select',
    category: 'advanced',
    values: [
      { value: 'junior', label: 'Junior', count: 420 },
      { value: 'mid', label: 'Mid-Level', count: 680 },
      { value: 'senior', label: 'Senior', count: 580 },
      { value: 'staff', label: 'Staff', count: 280 },
      { value: 'principal', label: 'Principal', count: 180 },
      { value: 'director', label: 'Director', count: 95 },
      { value: 'executive', label: 'Executive', count: 45 },
    ],
  },
  
  // Employment details
  employmentType: {
    id: 'employmentType',
    label: 'Employment Type',
    type: 'multi-select',
    category: 'secondary',
    values: [
      { value: 'full-time', label: 'Full-Time', count: 1650 },
      { value: 'part-time', label: 'Part-Time', count: 95 },
      { value: 'contractor', label: 'Contractor', count: 180 },
      { value: 'intern', label: 'Intern', count: 45 },
      { value: 'consultant', label: 'Consultant', count: 65 },
    ],
  },
  
  manager: {
    id: 'manager',
    label: 'Manager',
    type: 'multi-select',
    category: 'advanced',
    searchable: true,
    dependsOn: ['country', 'businessUnit', 'department'],
    values: [], // Populated dynamically based on other filters
  },
  
  team: {
    id: 'team',
    label: 'Team',
    type: 'multi-select',
    category: 'advanced',
    searchable: true,
    dependsOn: ['department', 'manager'],
    values: [], // Populated dynamically
  },
  
  // Demographics
  gender: {
    id: 'gender',
    label: 'Gender',
    type: 'multi-select',
    category: 'advanced',
    values: [
      { value: 'male', label: 'Male', count: 980 },
      { value: 'female', label: 'Female', count: 850 },
      { value: 'non-binary', label: 'Non-Binary', count: 42 },
      { value: 'prefer-not-to-say', label: 'Prefer Not to Say', count: 85 },
    ],
  },
  
  ageRange: {
    id: 'ageRange',
    label: 'Age Range',
    type: 'numeric-range',
    category: 'advanced',
    min: 18,
    max: 70,
  },
  
  // Tenure and performance
  tenure: {
    id: 'tenure',
    label: 'Tenure',
    type: 'multi-select',
    category: 'secondary',
    values: [
      { value: '0-6m', label: '0-6 months', count: 180 },
      { value: '6m-1y', label: '6 months - 1 year', count: 240 },
      { value: '1-2y', label: '1-2 years', count: 420 },
      { value: '2-3y', label: '2-3 years', count: 380 },
      { value: '3-5y', label: '3-5 years', count: 450 },
      { value: '5-10y', label: '5-10 years', count: 280 },
      { value: '10y+', label: '10+ years', count: 95 },
    ],
  },
  
  tenureBands: {
    id: 'tenureBands',
    label: 'Tenure Bands',
    type: 'multi-select',
    category: 'advanced',
    values: [
      { value: 'new-hire', label: 'New Hire (0-6 months)', count: 180 },
      { value: 'early-tenure', label: 'Early Tenure (6m-2y)', count: 660 },
      { value: 'established', label: 'Established (2-5y)', count: 830 },
      { value: 'veteran', label: 'Veteran (5+ years)', count: 375 },
    ],
  },
  
  performanceBand: {
    id: 'performanceBand',
    label: 'Performance Band',
    type: 'multi-select',
    category: 'advanced',
    values: [
      { value: 'exceeds', label: 'Exceeds Expectations', count: 420 },
      { value: 'meets', label: 'Meets Expectations', count: 1350 },
      { value: 'below', label: 'Below Expectations', count: 180 },
      { value: 'improvement-needed', label: 'Improvement Needed', count: 95 },
    ],
  },
  
  attritionRiskBand: {
    id: 'attritionRiskBand',
    label: 'Attrition Risk Band',
    type: 'multi-select',
    category: 'advanced',
    values: [
      { value: 'low', label: 'Low Risk (0-15%)', count: 1240 },
      { value: 'medium', label: 'Medium Risk (15-30%)', count: 580 },
      { value: 'high', label: 'High Risk (30-50%)', count: 180 },
      { value: 'critical', label: 'Critical Risk (50%+)', count: 45 },
    ],
  },
};

// Default filter presets
export const DEFAULT_FILTER_PRESETS = [
  {
    id: 'last-month-global',
    name: 'Last Month - Global',
    description: 'Last full month across all locations',
    isDefault: true,
    isPublic: true,
    filters: {
      dateRange: {
        start: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
        end: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      },
    },
  },
  {
    id: 'current-quarter',
    name: 'Current Quarter',
    description: 'Current quarter performance view',
    isPublic: true,
    filters: {
      dateRange: {
        start: new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3, 1),
        end: new Date(),
      },
    },
  },
  {
    id: 'high-performers',
    name: 'High Performers',
    description: 'Top performers across the organization',
    isPublic: true,
    filters: {
      performanceBand: ['exceeds'],
      attritionRiskBand: ['low', 'medium'],
    },
  },
  {
    id: 'retention-risk',
    name: 'Retention Risk',
    description: 'Employees at risk of leaving',
    isPublic: true,
    filters: {
      attritionRiskBand: ['high', 'critical'],
      performanceBand: ['exceeds', 'meets'],
    },
  },
];

// Filter categories for UI organization
export const FILTER_CATEGORIES = {
  primary: ['dateRange', 'country', 'businessUnit', 'department'],
  secondary: ['region', 'function', 'level', 'employmentType', 'tenure'],
  advanced: ['grade', 'manager', 'team', 'gender', 'ageRange', 'tenureBands', 'performanceBand', 'attritionRiskBand'],
};

// Quick filter configuration for top bar
export const QUICK_FILTERS = ['dateRange', 'country', 'businessUnit', 'department'];

// Time zone configuration
export const TIMEZONE_CONFIG = {
  default: 'Europe/Warsaw',
  supported: [
    'Europe/Warsaw',
    'UTC',
    'America/New_York',
    'America/Los_Angeles',
    'Asia/Singapore',
    'Asia/Tokyo',
  ],
};

// Locale configuration
export const LOCALE_CONFIG = {
  dateFormat: 'DD/MM/YYYY',
  timeFormat: 'HH:mm',
  numberFormat: {
    decimal: '.',
    thousands: ',',
    precision: 2,
  },
  currency: {
    symbol: '€',
    position: 'after',
  },
};